import * as Sentry from "@sentry/react";
import { logger } from "@/infrastructure/logger";
import { AppError } from "@/domain/errors/AppError";

/**
 * Initialize Sentry monitoring for error tracking and performance monitoring.
 * Only initializes in production environment with valid DSN.
 */
export function initMonitoring(): void {
	const dsn = import.meta.env.VITE_SENTRY_DSN;
	const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";
	const appVersion = import.meta.env.VITE_APP_VERSION || "0.0.0";

	// Only initialize in non-development environments with valid DSN
	if (!dsn || environment === "development") {
		logger.info(
			`[Monitoring] Sentry disabled (environment: ${environment}, dsn: ${dsn ? "present" : "missing"})`,
		);
		return;
	}

	try {
		Sentry.init({
			dsn,
			environment,
			release: `hulahoop-frontend@${appVersion}`,

			// Performance Monitoring
			tracesSampleRate: 0.1, // 10% of transactions
			tracePropagationTargets: [
				"localhost",
				/^https:\/\/api\.hulahoop\.pro/,
				/^https:\/\/hulahoop-pro-backend-[a-z0-9-]+-[a-z0-9]+\.a\.run\.app/,
			],

			// Session Replay (for debugging user sessions)
			replaysSessionSampleRate: 0.1, // 10% of normal sessions
			replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

			integrations: [
				Sentry.browserTracingIntegration({
					// Track React Router navigation
					enableInp: true,
				}),
				Sentry.replayIntegration({
					maskAllText: true, // Mask all text for privacy
					blockAllMedia: true, // Block images/videos
					maskAllInputs: true, // Mask input fields
				}),
				Sentry.browserProfilingIntegration(),
			],

			// Filter sensitive data and operational errors
			beforeSend(event, hint) {
				// Drop operational errors (404s, validation, business rules) — they are expected
				const exception = hint?.originalException;
				if (exception instanceof AppError && exception.isOperational) {
					return null;
				}

				// Remove passwords and tokens from request data
				if (event.request?.data) {
					const data = event.request.data as Record<string, unknown>;
					if (data.password) data.password = "[Filtered]";
					if (data.token) data.token = "[Filtered]";
					if (data.access_token) data.access_token = "[Filtered]";
					if (data.refresh_token) data.refresh_token = "[Filtered]";
				}

				// Filter Authorization headers
				if (event.request?.headers) {
					if (event.request.headers.Authorization) {
						event.request.headers.Authorization = "[Filtered]";
					}
				}

				return event;
			},

			// Ignore common non-critical errors
			ignoreErrors: [
				// Browser extensions
				"top.GLOBALS",
				"canvas.contentDocument",
				// Network errors (already logged)
				"Network request failed",
				"Failed to fetch",
				"NetworkError",
				// React errors that are handled
				"ResizeObserver loop limit exceeded",
				"ResizeObserver loop completed with undelivered notifications",
				// Non-Error promise rejections
				"Non-Error promise rejection captured",
				// Aborted requests (user navigation)
				"AbortError",
				"The operation was aborted",
			],

			// Don't report errors from browser extensions
			denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
		});

		logger.info(
			`[Monitoring] Sentry initialized (environment: ${environment}, release: hulahoop-frontend@${appVersion})`,
		);
	} catch (error) {
		logger.error("[Monitoring] Failed to initialize Sentry:", error);
	}
}

/**
 * Set user context for Sentry after authentication
 */
export function setUserContext(user: {
	id: string;
	email: string;
	firstName?: string;
	lastName?: string;
	organizationId?: string;
}): void {
	Sentry.setUser({
		id: user.id,
		email: user.email,
		username: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
	});

	// Set organization as tag for filtering
	if (user.organizationId) {
		Sentry.setTag("org_id", user.organizationId);
	}
}

/**
 * Clear user context (on logout)
 */
export function clearUserContext(): void {
	Sentry.setUser(null);
}

/**
 * Add custom context to Sentry events
 */
export function setContext(key: string, value: Record<string, unknown>): void {
	Sentry.setContext(key, value);
}

/**
 * Add a tag for filtering/searching
 */
export function setTag(key: string, value: string): void {
	Sentry.setTag(key, value);
}

/**
 * Add a breadcrumb for tracking user actions and API calls.
 * Breadcrumbs are shown in the timeline leading up to an error.
 */
export function addBreadcrumb(breadcrumb: {
	category: string;
	message: string;
	level?: "debug" | "info" | "warning" | "error";
	data?: Record<string, unknown>;
}): void {
	Sentry.addBreadcrumb({
		category: breadcrumb.category,
		message: breadcrumb.message,
		level: breadcrumb.level || "info",
		data: breadcrumb.data,
	});
}

/**
 * Manually capture an exception with optional fingerprinting and tags.
 * Fingerprinting controls how errors are grouped in Sentry.
 */
export function captureException(
	error: Error,
	context?: {
		fingerprint?: string[];
		tags?: Record<string, string>;
		extra?: Record<string, unknown>;
	},
): string | undefined {
	return Sentry.withScope((scope) => {
		if (context?.fingerprint) {
			scope.setFingerprint(context.fingerprint);
		}
		if (context?.tags) {
			Object.entries(context.tags).forEach(([key, value]) => {
				scope.setTag(key, value);
			});
		}
		if (context?.extra) {
			scope.setExtras(context.extra);
		}
		return Sentry.captureException(error);
	});
}

/**
 * Manually capture a message
 */
export function captureMessage(
	message: string,
	level: "fatal" | "error" | "warning" | "info" | "debug" = "info",
): string | undefined {
	return Sentry.captureMessage(message, level);
}
